import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  TampaBayRaysTCListItems,
  TampaBayRaysCheckoutTerms,
  TampaBayRaysAdditionalDetailsDisclaimerTC,
  TampaBayRaysFaqDisclaimer,
  AnalyticsHeader,
  raysMoreInfoButton,
  RaysSuiteInfoModal,
  raysGroupAreaImages,
  raysGroupAreaMapSectionNames,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import { DefaultRegistrationTerms } from "../../default_terms_and_conditions";

import Logo from "public/images/tampabayrays/logo.png";
import Background from "public/images/tampabayrays/background.jpg";
import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";

const Banner =
  "https://ss-leagues.s3.amazonaws.com/MLB/DIG263716_PartyArea_Banner_Image_1486x148_v2.png";

// Several suites share a set of photos — see `suiteImages` below for which.
const Suite16Images = [
  "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Suite%2016%20-%201.jpeg",
  "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Suite%2016%20-%202.jpeg",
];

const Suite24Images = [
  "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Baseline%20Suite%2024%20-%201.jpg",
  "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Baseline%20Suite%2024%20-%202.jpg",
  "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Baseline%20Suite%2024%20-%203.jpg",
];

const Suite30Images = [
  "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Outfield%20Suite%2030%20-%201.jpg",
  "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Outfield%20Suite%2030%20-%202.jpg",
];

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "tampabayrays_postseasonsuites",
  browserTitle: "Tampa Bay Rays Postseason Suites",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  mlbTeamId: 139,
  teamName: "Tampa Bay Rays",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10172-bayrays-sec",
    mapId: "blockmap2d",

    suiteImages: {
      S16: Suite16Images,
      // Suite 18 has no photos of its own — it reuses Suite 16's.
      S18: Suite16Images,

      S24: Suite24Images,
      // Suite 26 reuses Suite 24's photos.
      S26: Suite24Images,

      S30: Suite30Images,
      // Suites 31, 33 and 38 reuse Suite 30's photos.
      S31: Suite30Images,
      S33: Suite30Images,
      S38: Suite30Images,

      S9: [
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Suite%209%20-%201.jpeg",
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Suite%209%20-%202.jpeg",
      ],
      S35: [
        "https://ss-leagues.s3.amazonaws.com/MLB/Tampa%20Bay%20Rays/Suite%2035%20-%201.jpeg",
      ],

      // Daiquiri Deck, Patio Boxes, RIPA Bullpen Box, Seminole Hard Rock 162 Landing
      ...raysGroupAreaImages,
    },

    // the group areas have no node on the venue map
    hide3dViewFromSlider: raysGroupAreaMapSectionNames,
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    hideViewTicketsInThankYouPage: true,

    isCheckoutAdditionalDetailsEnabled: true,
    isPerksEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    googleTagManagerId: "",
    AnalyticsHeader: AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://www.mlb.com/rays/tickets/my-tickets",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",
  shouldUseInternalLogin: false,

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: Logo.src,
    findGamesBannerImage: Banner,
    authBackgroundImage: Background.src,
    checkoutStatusPageImage: Background.src,
    giftCardStandalonePageImage: Background.src,
    logoImage: Logo.src + "?width=128&height=66",
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    hidePackageBalanceOnCheckout: false,
    hideServiceFee: false,
    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    inventoryCardSettings: {
      isPBLineItemsVisible: true,
      seatCountLabel: "Tickets Included",
      viewSuiteButtonLabel: "View Images",
      inventorySuitePackageInfo: "$600",
      pbLineItemsValue: 5,
      hideFBPackageLineItem: true,

      suiteLevelInventoryCard: {
        customButtons: [raysMoreInfoButton],
        renderBody: [{ id: 1, component: () => <RaysSuiteInfoModal /> }],
      },

      customLineItems: [
        {
          label: "2027 Suite/Party Area Deposits",
          value: "$2,000",
          eventCodes: ["8081", "8082", "8083"],
        },
        {
          label: "2027 Suite/Party Area Deposits",
          value: "$1,000",
          eventCodes: ["8084", "8085", "8086", "8087"],
        },
        {
          label: "2027 Suite/Party Area Deposits",
          value: "$6,000",
          eventCodes: ["8088", "8089", "8090", "8091"],
        },
      ],
    },
    inventoryContainer: {
      emptyStateTitle: "Please select another game.",
      emptyStateDesc: " ",
      getGridCellHeight: (arg) => (arg?.mdDown ? 260 : 180),
    },
    checkoutPerkSubtotals: {
      ticketTotalLabel: "Total Ticket Balance",
      serviceFeeLabel: "Order Service Fee",
    },

    perks: {
      hideAddOnsViewer: true,
      hideCateringPerkItem: true,

      // cart "Deposit" line — the perk's seatCount is the number of $1,000
      // deposits for the round: ALDS (2), ALCS (4), WS (6)
      perkItemOverrides: [
        {
          matchTitle: "Deposit",
          title: "2027 Suite/Party Area Deposits",
          seatCountLabel: "({count}) $1,000 Deposits",
        },
      ],
    },

    eventCardSettings: {
      soldOutTagLabel:
        "Limited Inventory. Contact groupsales@raysbaseball.com for options.",
    },
  },

  colors: {
    primary: "#092C5C",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <TampaBayRaysTCListItems />,
    checkoutTermsAndCondition: <TampaBayRaysCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    additionalDetailsDisclaimerTC: (
      <TampaBayRaysAdditionalDetailsDisclaimerTC />
    ),
    purchaseSeats: {
      chooseAddOnsTabTitle: "Confirm Tickets",
      progressBarSuccessText:
        "You're good to go! Add more games or select Continue to view seating options.",
      progressBarAditionalText: " to continue!",
      faqDisclaimer: <TampaBayRaysFaqDisclaimer />,
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your Tampa Bay Rays Suites confirmation was successful! A representative will be in touch shortly to follow up with any questions you may have.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
